import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControlLabel, Checkbox, Chip, Alert, CircularProgress, Tooltip,
  FormGroup, Divider, Switch, Grid, InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as SuperAdminIcon,
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// All available pages with their labels, grouped by category
const ALL_PAGES = [
  { id: 'overview',         label: 'Dashboard Overview',       category: 'General' },
  { id: 'students',         label: 'Student Management',       category: 'Students' },
  { id: 'registrations',    label: 'Student Registrations',    category: 'Students' },
  { id: 'card-requests',    label: 'Student Card Requests',    category: 'Students' },
  { id: 'grade-reclamations', label: 'Réclamations Notes',     category: 'Students' },
  { id: 'masterform',         label: 'Demandes Master',        category: 'Students' },
  { id: 'modules',             label: 'Module Management',        category: 'Academic' },
  { id: 'pedagogy-structure',  label: 'Structure Pédagogique',    category: 'Academic' },
  { id: 'documents',            label: 'Documents & Attestations', category: 'System' },
  { id: 'auth-logs',            label: 'Journal de Connexion',     category: 'System' },
  { id: 'app-settings',        label: 'Paramètres Application',   category: 'System' },
  { id: 'course-schedules',    label: 'Course Program',           category: 'Academic' },
  { id: 'exams',               label: 'Planning Examens',         category: 'Academic' },
  { id: 'groups',              label: 'Gestion des Groupes',      category: 'Academic' },
  { id: 'laureats',            label: 'Gestion des Lauréats',     category: 'Academic' },
  { id: 'hr',               label: 'Ressources Humaines',      category: 'HR' },
  { id: 'exam-stats',        label: 'Statistiques Examens',     category: 'Academic' },
  { id: 'statistics',       label: 'Statistiques',             category: 'System' },
  { id: 'sync',             label: 'Data Synchronization',     category: 'System' },
  { id: 'system',           label: 'System Health',            category: 'System' },
];

const CATEGORIES = ['General', 'Students', 'Academic', 'HR', 'System'];

const ROLE_OPTIONS = [
  { value: 'SCOLARITE',    label: 'Scolarité' },
  { value: 'STATISTIQUES', label: 'Statistiques' },
  { value: 'RH_MANAGER',  label: 'RH Manager' },
  { value: 'READONLY',    label: 'Read Only' },
  { value: 'CUSTOM',      label: 'Custom' },
];

const getRoleChip = (role) => {
  const map = {
    SUPER_ADMIN:    { label: 'Super Admin',    color: '#c0392b', bg: '#fdecea' },
    RH_MANAGER:     { label: 'RH Manager',     color: '#e67e22', bg: '#fef5e7' },
    SCOLARITE:      { label: 'Scolarité',      color: '#2980b9', bg: '#eaf4fc' },
    STATISTIQUES:   { label: 'Statistiques',   color: '#8e44ad', bg: '#f5eef8' },
    READONLY:       { label: 'Read Only',      color: '#7f8c8d', bg: '#f2f3f4' },
    CUSTOM:         { label: 'Custom',         color: '#16a085', bg: '#e8f8f5' },
  };
  const style = map[role] || { label: role, color: '#555', bg: '#eee' };
  return (
    <Chip
      label={style.label}
      size="small"
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, border: `1px solid ${style.color}40` }}
    />
  );
};

const EMPTY_FORM = { username: '', password: '', full_name: '', role: 'SCOLARITE', permissions: [], is_active: true };

const AdminManagement = ({ currentAdminUsername }) => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null); // null = create mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState(null); // admin object to delete

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getAdmins();
      setAdmins(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  const openCreate = () => {
    setEditingAdmin(null);
    setForm(EMPTY_FORM);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEdit = (admin) => {
    setEditingAdmin(admin);
    setForm({
      username: admin.username,
      password: '',
      full_name: admin.full_name || '',
      role: admin.role,
      permissions: admin.permissions || [],
      is_active: admin.is_active,
    });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const togglePermission = (pageId) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(pageId)
        ? prev.permissions.filter(p => p !== pageId)
        : [...prev.permissions, pageId]
    }));
  };

  const toggleCategory = (category) => {
    const catPages = ALL_PAGES.filter(p => p.category === category).map(p => p.id);
    const allSelected = catPages.every(id => form.permissions.includes(id));
    setForm(prev => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter(p => !catPages.includes(p))
        : [...new Set([...prev.permissions, ...catPages])]
    }));
  };

  const handleSave = async () => {
    if (!editingAdmin && !form.password) {
      return setError('Password is required when creating an admin');
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        username: form.username,
        full_name: form.full_name,
        role: form.role,
        permissions: form.role === 'SUPER_ADMIN' ? [] : form.permissions,
        is_active: form.is_active,
        ...(form.password ? { password: form.password } : {}),
      };
      if (editingAdmin) {
        await adminAPI.updateAdmin(editingAdmin.id, payload);
        setSuccess(`Admin "${form.username}" updated successfully`);
      } else {
        await adminAPI.createAdmin({ ...payload, password: form.password });
        setSuccess(`Admin "${form.username}" created successfully`);
      }
      setDialogOpen(false);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save admin');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await adminAPI.deleteAdmin(deleteDialog.id);
      setSuccess(`Admin "${deleteDialog.username}" deleted`);
      setDeleteDialog(null);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete admin');
      setDeleteDialog(null);
    }
  };

  const isSuperAdmin = (role) => role === 'SUPER_ADMIN';

  // Stats
  const activeCount = admins.filter(a => a.is_active).length;
  const superCount  = admins.filter(a => a.role === 'SUPER_ADMIN').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="700" color="#c0392b">
            Admin Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create admins and control their page access
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: '#c0392b', '&:hover': { bgcolor: '#a93226' }, borderRadius: 2 }}
        >
          New Admin
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Admins', value: admins.length, color: '#3498db' },
          { label: 'Active',       value: activeCount,   color: '#27ae60' },
          { label: 'Super Admins', value: superCount,    color: '#c0392b' },
        ].map(s => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Card sx={{ borderRadius: 3, borderLeft: `4px solid ${s.color}` }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Typography variant="h4" fontWeight="700" color={s.color}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Admins Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
              <TableCell sx={{ fontWeight: 700 }}>Admin</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pages Access</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : admins.map(adm => (
              <TableRow key={adm.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: isSuperAdmin(adm.role) ? '#c0392b' : '#3498db', fontSize: '0.9rem' }}>
                      {adm.username.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="600">{adm.username}</Typography>
                      <Typography variant="caption" color="text.secondary">{adm.full_name || '—'}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{getRoleChip(adm.role)}</TableCell>
                <TableCell>
                  {isSuperAdmin(adm.role) ? (
                    <Chip icon={<ShieldIcon />} label="All pages" size="small" color="error" variant="outlined" />
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(adm.permissions || []).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">No access</Typography>
                      ) : (adm.permissions || []).slice(0, 3).map(p => (
                        <Chip key={p} label={ALL_PAGES.find(x => x.id === p)?.label || p} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      ))}
                      {(adm.permissions || []).length > 3 && (
                        <Chip label={`+${adm.permissions.length - 3} more`} size="small" sx={{ fontSize: '0.7rem', bgcolor: '#f0f0f0' }} />
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={adm.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={adm.is_active ? 'success' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(adm)} sx={{ color: '#3498db' }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {adm.username !== currentAdminUsername && (
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setDeleteDialog(adm)} sx={{ color: '#e74c3c' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid #eee' }}>
          {editingAdmin ? `Edit Admin: ${editingAdmin.username}` : 'Create New Admin'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Username" size="small"
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                disabled={!!editingAdmin}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label={editingAdmin ? 'New Password (leave blank to keep)' : 'Password'}
                size="small" type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(v => !v)}>
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Full Name" size="small"
                value={form.full_name}
                onChange={e => setForm({ ...form, full_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Role" size="small" select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
                SelectProps={{ native: true }}
              >
                {ROLE_OPTIONS.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </TextField>
            </Grid>
            {editingAdmin && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} color="success" />}
                  label="Active"
                />
              </Grid>
            )}
          </Grid>

          {/* Permissions — hidden for SUPER_ADMIN */}
          {form.role !== 'SUPER_ADMIN' && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" fontWeight="700" gutterBottom>
                Page Permissions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which pages this admin can access.
              </Typography>
              {CATEGORIES.map(cat => {
                const catPages = ALL_PAGES.filter(p => p.category === cat);
                const allSelected = catPages.every(p => form.permissions.includes(p.id));
                const someSelected = catPages.some(p => form.permissions.includes(p.id));
                return (
                  <Box key={cat} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        onChange={() => toggleCategory(cat)}
                      />
                      <Typography variant="body2" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                        {cat}
                      </Typography>
                    </Box>
                    <FormGroup row sx={{ ml: 3 }}>
                      {catPages.map(page => (
                        <FormControlLabel
                          key={page.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={form.permissions.includes(page.id)}
                              onChange={() => togglePermission(page.id)}
                            />
                          }
                          label={<Typography variant="body2">{page.label}</Typography>}
                          sx={{ minWidth: 200, mb: 0.5 }}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                );
              })}
            </Box>
          )}
          {form.role === 'SUPER_ADMIN' && (
            <Alert severity="info" sx={{ mt: 2 }} icon={<SuperAdminIcon />}>
              Super Admins automatically have access to all pages including Admin Management.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.username}
            sx={{ bgcolor: '#c0392b', '&:hover': { bgcolor: '#a93226' } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : (editingAdmin ? 'Save Changes' : 'Create Admin')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight="700">Delete Admin</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog?.username}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
